import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Input, Radio, ConfigProvider } from 'antd';
import { AddressSuggestions, PartySuggestions, FioSuggestions } from 'react-dadata';
import 'react-dadata/dist/react-dadata.css';
import lowerCase from 'lodash/lowerCase';

import PhoneCountryMask from './PhoneCountryMask';
import {
    ADDRESS_FIELD_NAMES,
    addressActions,
    addressSelectors,
    fetchCountries,
} from '../../features/address';
import { DADATA_API_KEY } from '../../config/secrets';
import './address-form-cart.css';

const AddressFormCart = ({ userData }) => {
    const dispatch = useDispatch();
    const fields = useSelector(addressSelectors.selectAddressFields);
    const isFetching = useSelector(addressSelectors.selectIsCountriesFetching);
    const countries = useSelector(addressSelectors.selectCountries);

    const [isJuridic, setIsJuridic] = useState(fields[ADDRESS_FIELD_NAMES.IS_JURIDIC] !== false);
    const [errors, setErrors] = useState({});

    const validateFieldsWithData = useCallback((fieldsToValidate) => {
        const validationErrors = {};

        if (!fieldsToValidate[ADDRESS_FIELD_NAMES.FIRSTNAME])
            validationErrors[ADDRESS_FIELD_NAMES.FIRSTNAME] = 'обязательно для заполнения';
        else if (!fieldsToValidate[ADDRESS_FIELD_NAMES.FIRSTNAME].match(/^[a-zA-Zа-яА-ЯёЁ\-\s]+$/))
            validationErrors[ADDRESS_FIELD_NAMES.FIRSTNAME] = 'допускаются буквы, пробел и дефис';

        if (!fieldsToValidate[ADDRESS_FIELD_NAMES.LASTNAME])
            validationErrors[ADDRESS_FIELD_NAMES.LASTNAME] = 'обязательно для заполнения';
        else if (!fieldsToValidate[ADDRESS_FIELD_NAMES.LASTNAME].match(/^[a-zA-Zа-яА-ЯёЁ\-\s]+$/))
            validationErrors[ADDRESS_FIELD_NAMES.LASTNAME] = 'допускаются буквы, пробел и дефис';

        if (!fieldsToValidate[ADDRESS_FIELD_NAMES.PHONE])
            validationErrors[ADDRESS_FIELD_NAMES.PHONE] = 'обязательно для заполнения';
        else if (fieldsToValidate[ADDRESS_FIELD_NAMES.PHONE].indexOf('_') >= 0)
            validationErrors[ADDRESS_FIELD_NAMES.PHONE] = 'введите номер полностью';

        if (fieldsToValidate[ADDRESS_FIELD_NAMES.EMAIL]) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(fieldsToValidate[ADDRESS_FIELD_NAMES.EMAIL])) {
                validationErrors[ADDRESS_FIELD_NAMES.EMAIL] = 'неверный формат эл.почты';
            }
        }

        if (fieldsToValidate[ADDRESS_FIELD_NAMES.IS_JURIDIC]) {
            if (!fieldsToValidate[ADDRESS_FIELD_NAMES.COMPANY])
                validationErrors[ADDRESS_FIELD_NAMES.COMPANY] = 'обязательно для заполнения';

            if (!fieldsToValidate[ADDRESS_FIELD_NAMES.COMPANY_JOB_TITLE])
                validationErrors[ADDRESS_FIELD_NAMES.COMPANY_JOB_TITLE] = 'обязательно для заполнения';

            if (fieldsToValidate[ADDRESS_FIELD_NAMES.COMPANY_INN] && !/^\d{10}$/.test(fieldsToValidate[ADDRESS_FIELD_NAMES.COMPANY_INN])) {
                validationErrors[ADDRESS_FIELD_NAMES.COMPANY_INN] = 'ИНН должен содержать 10 цифр';
            }

            if (fieldsToValidate[ADDRESS_FIELD_NAMES.COMPANY_KPP] && !/^\d{9}$/.test(fieldsToValidate[ADDRESS_FIELD_NAMES.COMPANY_KPP])) {
                validationErrors[ADDRESS_FIELD_NAMES.COMPANY_KPP] = 'КПП должен содержать 9 цифр';
            }

            if (fieldsToValidate[ADDRESS_FIELD_NAMES.COMPANY_OGRN] && !/^\d{13}$/.test(fieldsToValidate[ADDRESS_FIELD_NAMES.COMPANY_OGRN])) {
                validationErrors[ADDRESS_FIELD_NAMES.COMPANY_OGRN] = 'ОГРН должен содержать 13 цифр';
            }
        }

        const hasErrors = Object.keys(validationErrors).length > 0;
        return { validationErrors, hasErrors };
    }, []);

    const updateFields = useCallback((newFields) => {
        const { validationErrors, hasErrors } = validateFieldsWithData(newFields);
        const fieldsWithErrors = { ...newFields, hasErrors };

        dispatch(addressActions.setAddressFields(fieldsWithErrors));
        setErrors(validationErrors);
    }, [dispatch, validateFieldsWithData]);

    useEffect(() => {
        if (!countries || !countries.countries) {
            dispatch(fetchCountries());
        }
    }, []);

    useEffect(() => {
        if (!countries || !countries.countries) {
            dispatch(fetchCountries());
        }
    }, []);

    useEffect(() => {
        const updatedFields = { ...fields, [ADDRESS_FIELD_NAMES.IS_JURIDIC]: isJuridic };
        updateFields(updatedFields);
    }, [isJuridic]);

    let listPhoneCountries = [];
    if (!isFetching && countries && countries.countries) {
        listPhoneCountries = Object.values(countries.countries).map((element, index) => ({
            key: index,
            text: element.country_ru,
            flag: lowerCase(element.iso),
            value: element.iso,
            className: "dropdown-item",
            phone_mask: element.phone_mask,
        }));
    }

    const setCompanyField = (e) => {
        const updatedFields = {
            ...fields,
            [ADDRESS_FIELD_NAMES.COMPANY]: e.target.value,
            [ADDRESS_FIELD_NAMES.COMPANY_INN]: '',
            [ADDRESS_FIELD_NAMES.COMPANY_KPP]: '',
            [ADDRESS_FIELD_NAMES.COMPANY_OGRN]: '',
            [ADDRESS_FIELD_NAMES.COMPANY_CHIEF_NAME]: '',
            [ADDRESS_FIELD_NAMES.COMPANY_LEGAL_ADDR]: '',
        };
        updateFields(updatedFields);
    };

    const setDaDataCompanyField = (e) => {
        const updatedFields = {
            ...fields,
            [ADDRESS_FIELD_NAMES.COMPANY]: e.value,
            [ADDRESS_FIELD_NAMES.COMPANY_INN]: e.data.inn || '',
            [ADDRESS_FIELD_NAMES.COMPANY_KPP]: e.data.kpp || '',
            [ADDRESS_FIELD_NAMES.COMPANY_OGRN]: e.data.ogrn || '',
            [ADDRESS_FIELD_NAMES.COMPANY_CHIEF_NAME]: e.data.management?.name || '',
            [ADDRESS_FIELD_NAMES.COMPANY_LEGAL_ADDR]: e.data.address?.unrestricted_value || '',
        };
        updateFields(updatedFields);
    };

    const setField = (e) => {
        const updatedFields = {
            ...fields,
            [e.target.name]: e.target.value,
        };
        updateFields(updatedFields);
    };

    const setDirectField = (name, data) => {
        const updatedFields = {
            ...fields,
            [name]: data,
        };
        updateFields(updatedFields);
    };

    return (
        <ConfigProvider
            theme={{
                token: {
                    colorPrimary: '#D00202',
                    colorText: '#000000',
                    borderRadius: 4,
                },
                components: {
                    Input: {
                        colorBgContainer: '#ffffff',
                        colorText: '#000000',
                        colorBorder: '#d9d9d9',
                    },
                    Radio: {
                        colorPrimary: '#D00202',
                    }
                }
            }}
        >
            <div className="address-form-cart">
                <div className="entity-type-selector">
                    <Radio.Group
                        value={isJuridic ? 'juridic' : 'individual'}
                        onChange={(e) => setIsJuridic(e.target.value === 'juridic')}
                    >
                        <Radio value="juridic">Юридическое лицо</Radio>
                        <Radio value="individual">Физическое лицо</Radio>
                    </Radio.Group>
                </div>

                <div className="address-form-grid">
                    <div className="form-row">
                        <div className="form-field">
                            <FioSuggestions
                                containerClassName="dadata-input-wrapper"
                                inputProps={{
                                    placeholder: 'Имя *',
                                    name: ADDRESS_FIELD_NAMES.FIRSTNAME,
                                    value: fields[ADDRESS_FIELD_NAMES.FIRSTNAME] || '',
                                    onChange: setField,
                                }}
                                hintText="Выберите вариант или продолжите ввод ..."
                                filterParts={['NAME']}
                                count={4}
                                token={DADATA_API_KEY}
                                defaultQuery={fields[ADDRESS_FIELD_NAMES.FIRSTNAME]}
                                onChange={(e) => setDirectField(ADDRESS_FIELD_NAMES.FIRSTNAME, e.value)}
                            />
                            {errors[ADDRESS_FIELD_NAMES.FIRSTNAME] && (
                                <span className="field-error">{errors[ADDRESS_FIELD_NAMES.FIRSTNAME]}</span>
                            )}
                        </div>
                        <div className="form-field">
                            <FioSuggestions
                                containerClassName="dadata-input-wrapper"
                                inputProps={{
                                    placeholder: 'Фамилия *',
                                    name: ADDRESS_FIELD_NAMES.LASTNAME,
                                    value: fields[ADDRESS_FIELD_NAMES.LASTNAME] || '',
                                    onChange: setField,
                                }}
                                hintText="Выберите вариант или продолжите ввод ..."
                                filterParts={['SURNAME']}
                                count={4}
                                token={DADATA_API_KEY}
                                defaultQuery={fields[ADDRESS_FIELD_NAMES.LASTNAME]}
                                onChange={(e) => setDirectField(ADDRESS_FIELD_NAMES.LASTNAME, e.value)}
                            />
                            {errors[ADDRESS_FIELD_NAMES.LASTNAME] && (
                                <span className="field-error">{errors[ADDRESS_FIELD_NAMES.LASTNAME]}</span>
                            )}
                        </div>
                    </div>

                    <div className="form-row phone-row">
                        <div className="form-field full-width">
                            <PhoneCountryMask
                                extraClass="phone-input-cart"
                                countries={listPhoneCountries}
                                loading={isFetching}
                                country={fields[ADDRESS_FIELD_NAMES.PHONE_COUNTRY]}
                                phone={fields[ADDRESS_FIELD_NAMES.PHONE]}
                                handleChangeField={setDirectField}
                                nameCountry={ADDRESS_FIELD_NAMES.PHONE_COUNTRY}
                                namePhone={ADDRESS_FIELD_NAMES.PHONE}
                                fluid={true}
                            />
                            {errors[ADDRESS_FIELD_NAMES.PHONE] && (
                                <span className="field-error">{errors[ADDRESS_FIELD_NAMES.PHONE]}</span>
                            )}
                        </div>
                    </div>

                    <div className="form-row email-row">
                        <div className="form-field full-width">
                            <Input
                                placeholder="Электронная почта"
                                name={ADDRESS_FIELD_NAMES.EMAIL}
                                value={fields[ADDRESS_FIELD_NAMES.EMAIL]}
                                onChange={setField}
                            />
                            {errors[ADDRESS_FIELD_NAMES.EMAIL] && (
                                <span className="field-error">{errors[ADDRESS_FIELD_NAMES.EMAIL]}</span>
                            )}
                        </div>
                    </div>

                    {isJuridic && (
                        <>
                            <div className="form-row">
                                <div className="form-field full-width">
                                    <PartySuggestions
                                        containerClassName="dadata-input-wrapper"
                                        inputProps={{
                                            placeholder: 'Название компании или ИП *',
                                            name: ADDRESS_FIELD_NAMES.COMPANY,
                                            value: fields[ADDRESS_FIELD_NAMES.COMPANY] || '',
                                            onChange: setCompanyField,
                                        }}
                                        hintText="Выберите вариант или продолжите ввод ..."
                                        filterStatus={['ACTIVE']}
                                        count={4}
                                        token={DADATA_API_KEY}
                                        defaultQuery={fields[ADDRESS_FIELD_NAMES.COMPANY]}
                                        onChange={setDaDataCompanyField}
                                    />
                                    {errors[ADDRESS_FIELD_NAMES.COMPANY] && (
                                        <span className="field-error">{errors[ADDRESS_FIELD_NAMES.COMPANY]}</span>
                                    )}
                                </div>
                            </div>

                            <div className="form-row three-columns">
                                <div className="form-field">
                                    <Input
                                        placeholder="ИНН"
                                        name={ADDRESS_FIELD_NAMES.COMPANY_INN}
                                        value={fields[ADDRESS_FIELD_NAMES.COMPANY_INN]}
                                        onChange={setField}
                                        disabled={!!fields[ADDRESS_FIELD_NAMES.COMPANY_INN]}
                                    />
                                    {errors[ADDRESS_FIELD_NAMES.COMPANY_INN] && (
                                        <span className="field-error">{errors[ADDRESS_FIELD_NAMES.COMPANY_INN]}</span>
                                    )}
                                </div>
                                <div className="form-field">
                                    <Input
                                        placeholder="КПП"
                                        name={ADDRESS_FIELD_NAMES.COMPANY_KPP}
                                        value={fields[ADDRESS_FIELD_NAMES.COMPANY_KPP]}
                                        onChange={setField}
                                        disabled={!!fields[ADDRESS_FIELD_NAMES.COMPANY_KPP]}
                                    />
                                    {errors[ADDRESS_FIELD_NAMES.COMPANY_KPP] && (
                                        <span className="field-error">{errors[ADDRESS_FIELD_NAMES.COMPANY_KPP]}</span>
                                    )}
                                </div>
                                <div className="form-field">
                                    <Input
                                        placeholder="ОГРН"
                                        name={ADDRESS_FIELD_NAMES.COMPANY_OGRN}
                                        value={fields[ADDRESS_FIELD_NAMES.COMPANY_OGRN]}
                                        onChange={setField}
                                        disabled={!!fields[ADDRESS_FIELD_NAMES.COMPANY_OGRN]}
                                    />
                                    {errors[ADDRESS_FIELD_NAMES.COMPANY_OGRN] && (
                                        <span className="field-error">{errors[ADDRESS_FIELD_NAMES.COMPANY_OGRN]}</span>
                                    )}
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-field">
                                    <Input
                                        placeholder="Руководитель"
                                        name={ADDRESS_FIELD_NAMES.COMPANY_CHIEF_NAME}
                                        value={fields[ADDRESS_FIELD_NAMES.COMPANY_CHIEF_NAME]}
                                        onChange={setField}
                                        disabled={!!fields[ADDRESS_FIELD_NAMES.COMPANY_CHIEF_NAME]}
                                    />
                                </div>
                                <div className="form-field">
                                    <FioSuggestions
                                        containerClassName="dadata-input-wrapper"
                                        inputProps={{
                                            placeholder: 'ФИО',
                                            name: ADDRESS_FIELD_NAMES.COMPANY_CONTACT_NAME,
                                            value: fields[ADDRESS_FIELD_NAMES.COMPANY_CONTACT_NAME] || '',
                                            onChange: setField,
                                        }}
                                        hintText="Выберите вариант или продолжите ввод ..."
                                        filterParts={['NAME', 'SURNAME', 'PATRONYMIC']}
                                        count={4}
                                        token={DADATA_API_KEY}
                                        defaultQuery={fields[ADDRESS_FIELD_NAMES.COMPANY_CONTACT_NAME]}
                                        onChange={(e) => setDirectField(ADDRESS_FIELD_NAMES.COMPANY_CONTACT_NAME, e.value)}
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-field full-width">
                                    <AddressSuggestions
                                        containerClassName="dadata-input-wrapper"
                                        inputProps={{
                                            placeholder: 'Юридический адрес',
                                            name: ADDRESS_FIELD_NAMES.COMPANY_LEGAL_ADDR,
                                            value: fields[ADDRESS_FIELD_NAMES.COMPANY_LEGAL_ADDR] || '',
                                            onChange: setField,
                                        }}
                                        defaultQuery={fields[ADDRESS_FIELD_NAMES.COMPANY_LEGAL_ADDR]}
                                        hintText="Выберите вариант или продолжите ввод ..."
                                        count={4}
                                        token={DADATA_API_KEY}
                                        onChange={(e) => setDirectField(ADDRESS_FIELD_NAMES.COMPANY_LEGAL_ADDR, e.unrestricted_value)}
                                        filterFromBound="city"
                                        filterToBound="house"
                                        disabled={!!fields[ADDRESS_FIELD_NAMES.COMPANY_LEGAL_ADDR]}
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-field full-width">
                                    <AddressSuggestions
                                        containerClassName="dadata-input-wrapper"
                                        inputProps={{
                                            placeholder: 'Почтовый адрес',
                                            name: ADDRESS_FIELD_NAMES.COMPANY_POST_ADDR,
                                            value: fields[ADDRESS_FIELD_NAMES.COMPANY_POST_ADDR] || '',
                                            onChange: setField,
                                        }}
                                        defaultQuery={fields[ADDRESS_FIELD_NAMES.COMPANY_POST_ADDR]}
                                        hintText="Выберите вариант или продолжите ввод ..."
                                        count={4}
                                        token={DADATA_API_KEY}
                                        onChange={(e) => setDirectField(ADDRESS_FIELD_NAMES.COMPANY_POST_ADDR, e.unrestricted_value)}
                                        filterFromBound="city"
                                        filterToBound="house"
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-field full-width">
                                    <Input
                                        placeholder="Сфера деятельности"
                                        name={ADDRESS_FIELD_NAMES.COMPANY_SCOPE}
                                        value={fields[ADDRESS_FIELD_NAMES.COMPANY_SCOPE]}
                                        onChange={setField}
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-field full-width">
                                    <Input
                                        placeholder="Кем являетесь / должность *"
                                        name={ADDRESS_FIELD_NAMES.COMPANY_JOB_TITLE}
                                        value={fields[ADDRESS_FIELD_NAMES.COMPANY_JOB_TITLE]}
                                        onChange={setField}
                                    />
                                    {errors[ADDRESS_FIELD_NAMES.COMPANY_JOB_TITLE] && (
                                        <span className="field-error">{errors[ADDRESS_FIELD_NAMES.COMPANY_JOB_TITLE]}</span>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </ConfigProvider>
    );
};

export default AddressFormCart;